import axios, {AxiosResponse} from 'axios'
import {PaginatedReportResult, REPORT_PAGE_SIZE} from '../inventory/components/reportTypes'

const API_URL = process.env.REACT_APP_THEME_API_URL

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface WarehouseOption {
  warehouseId: number
  warehouseName: string
}

export interface ProductOption {
  productId: number
  productName: string
}

export interface ReturnScrapRecord {
  productName: string
  skuCode: string
  unit: string
  returnQty: number
  scrapQty: number
  totalValue: number
  date: string
}

// ─── Options APIs ─────────────────────────────────────────────────────────────

export const getWarehouseOptions = async (
  companyId: string
): Promise<WarehouseOption[] | undefined> => {
  return await axios
    .get(`${API_URL}/warehouse/list/${companyId}`)
    .then((response: AxiosResponse<any[]>) => response.data)
    .then((data: any[]) =>
      data.map((w) => ({
        warehouseId: w.warehouse_id,
        warehouseName: w.warehouse_name,
      }))
    )
}

export const getProductOptions = async (
  companyId: string
): Promise<ProductOption[] | undefined> => {
  return await axios
    .get(`${API_URL}/product-catalogue/list/${companyId}`)
    .then((response: AxiosResponse<any[]>) => response.data)
    .then((data: any[]) =>
      data.map((p) => ({
        productId: p.product_id,
        productName: p.product_name,
      }))
    )
}

// ─── Report API ───────────────────────────────────────────────────────────────

export interface ReturnScrapReportParams {
  company_id: number
  fy_name: string
  from_date: string
  to_date: string
  warehouse_ids: number[] | null
  product_ids: number[] | null
  product_line: string
  movement_days: number
  page: number
  page_size: number
}

export const getReturnScrapReport = async (
  params: ReturnScrapReportParams
): Promise<PaginatedReportResult<ReturnScrapRecord> | undefined> => {
  try {
    const response = await axios.post(`${API_URL}/inventory/report/return-scrap`, params)
    return {
      records: (response.data.records || []).map((s: any) => ({
        productName: s.product_name,
        skuCode: s.sku,
        unit: s.uom,
        returnQty: s.return_qty ?? 0,
        scrapQty: s.scrap_qty ?? 0,
        totalValue: s.total_value ?? 0,
        date: s.date ?? s.return_date,
      })),
      page: response.data.page ?? params.page,
      pageSize: response.data.page_size ?? params.page_size ?? REPORT_PAGE_SIZE,
      totalRecords: response.data.total_records ?? 0,
      totalPages: response.data.total_pages ?? 1,
    }
  } catch (error: any) {
    if (error.response) {
      throw error.response.data
    }
  }
}
